import PropTypes from 'prop-types';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Popper from '@mui/material/Popper';
import Box from '@mui/material/Box';
import Autocomplete from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

// third party
import PopupState, { bindPopper, bindToggle } from 'material-ui-popup-state';

// project imports
import Transitions from 'ui-component/extended/Transitions';
import menuItems from 'menu-items';
import { customerApi, employeeApi } from 'services/api';

// assets
import { IconAdjustmentsHorizontal, IconSearch, IconX, IconCheck } from '@tabler/icons-react';

// ==============================|| SEARCH INPUT - MOBILE ||============================== //

function MobileSearch({ value, setValue, popupState, searchItems, navigate }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Autocomplete
      options={searchItems}
      getOptionLabel={(option) => option.title}
      inputValue={value}
      onInputChange={(event, newInputValue) => setValue(newInputValue)}
      onChange={(event, newValue) => {
        if (newValue && newValue.url) {
          navigate(newValue.url);
          setValue('');
          popupState.close();
        }
      }}
      renderInput={(params) => {
        const { InputProps, inputProps, ...otherParams } = params;
        return (
          <OutlinedInput
            ref={InputProps.ref}
            {...otherParams}
            id="input-search-header-mobile"
            placeholder="Search"
            inputProps={{
              ...inputProps,
              'aria-label': 'search',
              sx: {
                bgcolor: 'transparent',
                pl: 1,
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                color: isDark ? '#F8FAFC' : '#1E293B',
                '&::placeholder': { color: '#94A3B8', opacity: 1 }
              }
            }}
            startAdornment={
              <InputAdornment position="start">
                <IconSearch stroke={1.75} size={18} color="#64748B" />
              </InputAdornment>
            }
            endAdornment={
              <InputAdornment position="end">
                <Tooltip title="Close Search" arrow>
                  <ButtonBase
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '6px',
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#F1F5F9',
                      color: isDark ? '#94A3B8' : '#64748B',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(20, 184, 166, 0.2)' : '#F0FDFA',
                        color: '#0D9488'
                      }
                    }}
                    {...bindToggle(popupState)}
                  >
                    <IconX stroke={1.75} size={17} />
                  </ButtonBase>
                </Tooltip>
                {InputProps.endAdornment}
              </InputAdornment>
            }
            sx={{
              width: '100%',
              height: 40,
              bgcolor: isDark ? '#0F172A' : '#FFFFFF',
              borderRadius: '8px',
              px: 1.5,
              '& .MuiOutlinedInput-notchedOutline': {
                border: isDark ? '1px solid #334155' : '1px solid #CBD5E1'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isDark ? '#64748B' : '#94A3B8'
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#14B8A6',
                borderWidth: '1px'
              },
              '&.Mui-focused': {
                boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.15)'
              }
            }}
          />
        );
      }}
    />
  );
}

// ==============================|| SEARCH INPUT ||============================== //

export default function SearchSection() {
  const theme = useTheme();
  const [value, setValue] = useState('');
  const [dynamicOptions, setDynamicOptions] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all'); // 'all' | 'page' | 'customer' | 'employee'
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const navigate = useNavigate();

  const isFilterOpen = Boolean(filterAnchorEl);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSelectFilter = (cat) => {
    setFilterCategory(cat);
    setFilterAnchorEl(null);
  };

  const baseSearchItems = useMemo(() => {
    const items = [];
    const traverse = (item) => {
      if (item.url && item.title) {
        items.push({ title: 'Page: ' + item.title, url: item.url, id: item.id, type: 'page' });
      }
      if (item.children) {
        item.children.forEach(traverse);
      }
    };
    menuItems.items.forEach(traverse);
    return items;
  }, []);

  useEffect(() => {
    if (value.length >= 2) {
      Promise.all([
        customerApi.getAll({ mobile: value }),
        customerApi.getAll({ email: value }),
        employeeApi.getAll({ name: value })
      ])
        .then(([cMobile, cEmail, eName]) => {
          const newOpts = [];
          const addOpt = (arr, type, category, urlPrefix) => {
            arr.forEach((u) =>
              newOpts.push({
                title: `${type}: ${u.name || 'Unknown'} (${u.mobile || u.email || ''})`,
                url: urlPrefix,
                type: category
              })
            );
          };
          if (cMobile?.data?.data?.list) addOpt(cMobile.data.data.list, 'Customer', 'customer', '/smart-locker/user-customer');
          if (cEmail?.data?.data?.list) addOpt(cEmail.data.data.list, 'Customer', 'customer', '/smart-locker/user-customer');
          if (eName?.data?.data?.list) addOpt(eName.data.data.list, 'Employee', 'employee', '/smart-locker/user-employee');

          const unique = Array.from(new Set(newOpts.map((a) => a.title))).map((title) =>
            newOpts.find((a) => a.title === title)
          );
          setDynamicOptions(unique);
        })
        .catch((e) => {
          console.error(e);
          setDynamicOptions([]);
        });
    } else {
      setDynamicOptions([]);
    }
  }, [value]);

  const searchItems = useMemo(() => {
    const all = [...baseSearchItems, ...dynamicOptions];
    if (filterCategory === 'all') return all;
    return all.filter((item) => item.type === filterCategory);
  }, [baseSearchItems, dynamicOptions, filterCategory]);

  return (
    <>
      {/* Mobile Search Button & Popup */}
      <Box sx={{ display: { xs: 'block', md: 'none' }, ml: 1.5 }}>
        <PopupState variant="popper" popupId="header-mobile-search-popper">
          {(popupState) => (
            <>
              <Tooltip title="Search" arrow>
                <ButtonBase
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    bgcolor: '#F1F5F9',
                    border: '1px solid transparent',
                    color: '#64748B',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      bgcolor: '#F0FDFA',
                      color: '#0D9488',
                      borderColor: '#CCFBF1'
                    }
                  }}
                  {...bindToggle(popupState)}
                >
                  <IconSearch stroke={1.75} size={18} />
                </ButtonBase>
              </Tooltip>
              <Popper
                {...bindPopper(popupState)}
                transition
                sx={{ zIndex: 1200, width: '96%', top: '8px !important', px: { xs: 1.25, sm: 1.5 } }}
              >
                {({ TransitionProps }) => (
                  <Transitions type="zoom" {...TransitionProps} sx={{ transformOrigin: 'center left' }}>
                    <Card sx={{ 
                      bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.raised || '#272B33') : '#FFFFFF', 
                      border: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #CBD5E1', 
                      boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.08)', 
                      borderRadius: '12px' 
                    }}>
                      <Box sx={{ p: 1.5 }}>
                        <Grid container sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                          <Grid size="grow">
                            <MobileSearch
                              value={value}
                              setValue={setValue}
                              popupState={popupState}
                              searchItems={searchItems}
                              navigate={navigate}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Card>
                  </Transitions>
                )}
              </Popper>
            </>
          )}
        </PopupState>
      </Box>

      {/* Desktop Search Bar + Separate Filter Button */}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 2 }}>
        <Autocomplete
          options={searchItems}
          getOptionLabel={(option) => option.title}
          inputValue={value}
          onInputChange={(event, newInputValue) => setValue(newInputValue)}
          onChange={(event, newValue) => {
            if (newValue && newValue.url) {
              navigate(newValue.url);
              setValue('');
            }
          }}
          renderInput={(params) => {
            const { InputProps, inputProps, ...otherParams } = params;
            return (
              <OutlinedInput
                ref={InputProps.ref}
                {...otherParams}
                id="input-search-header"
                placeholder={filterCategory === 'all' ? 'Search' : `Search ${filterCategory}s...`}
                inputProps={{
                  ...inputProps,
                  'aria-label': 'search',
                  sx: {
                    bgcolor: 'transparent',
                    pl: 1,
                    py: 0,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '13px',
                    fontWeight: 400,
                    color: theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#1E293B',
                    '&::placeholder': {
                      color: theme.palette.mode === 'dark' ? (theme.palette.text?.placeholder || '#707886') : '#94A3B8',
                      opacity: 1
                    }
                  }
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <IconSearch stroke={1.75} size={18} color={theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#64748B'} />
                  </InputAdornment>
                }
                endAdornment={InputProps.endAdornment}
                aria-describedby="search-helper-text"
                sx={{
                  width: { md: 260, lg: 380 },
                  height: 40,
                  bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.input || '#1A1D23') : '#FFFFFF',
                  borderRadius: '8px',
                  px: 1.5,
                  transition: 'all 0.2s ease-in-out',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #CBD5E1'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.mode === 'dark' ? (theme.palette.border?.strong || 'rgba(255, 255, 255, 0.13)') : '#94A3B8'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#14B8A6',
                    borderWidth: '1px'
                  },
                  '&.Mui-focused': {
                    boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.15)'
                  }
                }}
              />
            );
          }}
        />

        {/* Separate Filter Button */}
        <Tooltip title="Search Filters" arrow>
          <ButtonBase
            aria-controls={isFilterOpen ? 'search-filter-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isFilterOpen ? 'true' : undefined}
            onClick={handleFilterClick}
            sx={{
              ml: 1,
              width: 38,
              height: 38,
              minWidth: 38,
              borderRadius: '8px',
              bgcolor: isFilterOpen || filterCategory !== 'all' 
                ? (theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15)' : '#F0FDFA') 
                : 'transparent',
              color: isFilterOpen || filterCategory !== 'all' ? '#14B8A6' : (theme.palette.mode === 'dark' ? (theme.palette.text?.secondary || '#A4ABB8') : '#475569'),
              transition: 'all 0.2s ease-in-out',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.hover || '#2A2E36') : '#F1F5F9',
                color: '#14B8A6'
              }
            }}
          >
            <IconAdjustmentsHorizontal stroke={1.5} size={20} />
          </ButtonBase>
        </Tooltip>

        {/* Filter Dropdown Menu */}
        <Menu
          id="search-filter-menu"
          anchorEl={filterAnchorEl}
          open={isFilterOpen}
          onClose={handleFilterClose}
          MenuListProps={{
            'aria-labelledby': 'search-filter-button',
            sx: { py: 0.5 }
          }}
          slotProps={{
            paper: {
              sx: {
                borderRadius: '12px',
                border: theme.palette.mode === 'dark' ? (theme.palette.border?.default ? `1px solid ${theme.palette.border.default}` : '1px solid rgba(255, 255, 255, 0.08)') : '1px solid #E2E8F0',
                bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.raised || '#272B33') : '#FFFFFF',
                boxShadow: theme.palette.mode === 'dark' ? '0 12px 32px rgba(0,0,0,0.45)' : '0 4px 20px rgba(0,0,0,0.08)',
                minWidth: 170,
                mt: 1
              }
            }
          }}
        >
          {[
            { id: 'all', label: 'All Results' },
            { id: 'page', label: 'Pages Only' },
            { id: 'customer', label: 'Customers Only' },
            { id: 'employee', label: 'Employees Only' }
          ].map((opt) => (
            <MenuItem
              key={opt.id}
              selected={filterCategory === opt.id}
              onClick={() => handleSelectFilter(opt.id)}
              sx={{
                py: 1,
                px: 1.5,
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                color: filterCategory === opt.id 
                  ? '#14B8A6' 
                  : (theme.palette.mode === 'dark' ? (theme.palette.text?.primary || '#ECEEF1') : '#1E293B'),
                bgcolor: filterCategory === opt.id 
                  ? (theme.palette.mode === 'dark' ? 'rgba(20, 184, 166, 0.15) !important' : '#F0FDFA !important') 
                  : 'transparent',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? (theme.palette.background?.hover || '#2A2E36') : '#F8FAFC'
                }
              }}
            >
              <ListItemText
                primary={opt.label}
                primaryTypographyProps={{
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                  fontWeight: filterCategory === opt.id ? 600 : 400
                }}
              />
              {filterCategory === opt.id && (
                <ListItemIcon sx={{ minWidth: 'auto', color: '#0D9488' }}>
                  <IconCheck stroke={1.75} size={16} />
                </ListItemIcon>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  );
}

MobileSearch.propTypes = {
  value: PropTypes.string,
  setValue: PropTypes.func,
  popupState: PropTypes.any,
  searchItems: PropTypes.array,
  navigate: PropTypes.func
};
